import React, { useContext, useEffect, useState } from "react";
import {
  Container,
  Navbar,
  Nav,
  Dropdown,
  Image,
  Button,
} from "react-bootstrap";
import { useHistory } from "react-router-dom";
import Loader from "./loader";
import chevronDownIcon from "../../assets/icons/chevron-down.png";
import chevronUpIcon from "../../assets/icons/chevron-up.png";
import { useTranslation } from "react-i18next";
import { ActualLangContext } from "../../contexts/ActualLangContext";
import { MegaMenu } from "./MegaMenu";
import { buildNavbarUrl } from "../../utils/navigationHelpers";
import { addItemToMenuOption } from "./menuHardcodedData";

export const MenuUsaDesk = ({
  logo,
  options,
  buttons,
  handleChange,
  checked,
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [prevHovering, setPrevHovering] = useState(null);
  const [scrollYPosition, setScrollYPosition] = useState(0);
  const history = useHistory();
  const { i18n } = useTranslation();
  const { actualLang, setLang } = useContext(ActualLangContext);

  // Manejar el scroll para cambiar el estilo del navbar
  useEffect(() => {
    const handleScroll = () => {
      setScrollYPosition(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Verificar si estamos en la página de índice de soluciones (no mostrar switch de idioma)
  // El toggle SÍ debe aparecer en páginas específicas como /soluciones/adopcion-ia
  const isInSolutionsIndexPage =
    window.location.pathname === "/solutions" ||
    window.location.pathname === "/soluciones";

  // Determinar si el navbar debe tener fondo sólido
  const shouldHaveSolidBackground = scrollYPosition > 600 || isHovering;

  // Handler para cuando el mouse sale del navbar
  const handleMouseLeave = () => {
    setPrevHovering(isHovering);
    setIsHovering(false);
    setTimeout(() => setPrevHovering(null), 400);
  };

  // Handler para cambio de idioma
  const handleLanguageSwitch = (event) => {
    handleChange(event);

    const newLang = checked ? 'en' : 'es';
    setLang(newLang);
    i18n.changeLanguage(newLang);
    localStorage.setItem('lang', newLang);

    let newUrl = window.location.pathname;
    // Rutas usan /en/ y /es/ (ej. microsoft-ecosystem) o /en-en/ y /es-es/
    if (newLang === 'es') {
      newUrl = newUrl.replace(/^\/en\//, '/es/').replace(/^\/en-en\//, '/es-es/');
    } else {
      newUrl = newUrl.replace(/^\/es\//, '/en/').replace(/^\/es-es\//, '/en-en/');
    }
    // Cambiar también solutions <-> soluciones en la URL
    if (newLang === 'en') {
      newUrl = newUrl.replace('/soluciones/', '/solutions/').replace('/soluciones', '/solutions');
    } else {
      newUrl = newUrl.replace('/solutions/', '/soluciones/').replace('/solutions', '/soluciones');
    }

    history.push(newUrl);
  };

  // Agregar el hardcodedItem al multipleOptions.data de la primera opción
  const optionsWithHardcoded = addItemToMenuOption(options, { targetIndex: 0 });

  return (
    <div className="d-none d-sm-none d-md-none d-lg-block">
      <Navbar
        expand="lg"
        className={`navbar p-3 transition ${shouldHaveSolidBackground
            ? "fixed-top bg-white navbar-light"
            : "bg-transparent position-absolute w-100 shadow-none"
          }`}
        onMouseLeave={handleMouseLeave}
      >
        <Container fluid className="p-0">
          <Navbar.Brand href={`/`}>
            {logo?.data ? (
              <img
                src={logo.data.attributes.url}
                alt={logo?.data.attributes.alternativeText}
                className="LogoHeader"
              ></img>
            ) : (
              <Loader />
            )}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse
            id="basic-navbar-nav"
            className="justify-content-end"
          >
            <Nav
              className=" justify-content-end align-items-center"
              activeKey={window.location.pathname}
            >
              {optionsWithHardcoded?.map((option) => {
                const hasSubMenu = option.multipleOptions.data.length > 0;
                const isDropdownActive = isHovering === option.id;

                return (
                  <React.Fragment key={option.id}>
                    {hasSubMenu ? (
                      <Dropdown
                        className={isDropdownActive ? "custom-dropdown show" : "custom-dropdown"}
                        onMouseEnter={() => setIsHovering(option.id)}
                      >
                        <Dropdown.Toggle
                          aria-expanded={isDropdownActive}
                          onClick={() => null}
                        >
                          {option.title}
                          <Image
                            src={chevronDownIcon}
                            alt="arrow icon"
                            className="inactive"
                          />
                          <Image
                            src={chevronUpIcon}
                            alt="arrow icon"
                            className="active"
                          />
                        </Dropdown.Toggle>
                        <div
                          className={
                            isDropdownActive
                              ? "dropdown-menu show"
                              : prevHovering === option.id
                                ? "dropdown-menu hidden"
                                : "dropdown-menu"
                          }
                        >
                          <MegaMenu
                            items={option.multipleOptions.data}
                            key={option.id}
                          />
                        </div>
                      </Dropdown>
                    ) : (
                      <Nav.Link href={buildNavbarUrl(option.href, actualLang)}>
                        {option.title}
                      </Nav.Link>
                    )}
                  </React.Fragment>
                );
              })}
              {buttons?.map((button, index) => (
                <Button key={index} variant={button.variant} href={button.href}>
                  {button.textButton}
                </Button>
              ))}
            </Nav>
            {!isInSolutionsIndexPage && (
              <LanguageSwitcher
                checked={checked}
                onChange={handleLanguageSwitch}
                scrollYPosition={scrollYPosition}
              />
            )}
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </div>
  );
};

/**
 * Componente para el switch de idioma EN/ES
 */
const LanguageSwitcher = ({ checked, onChange, scrollYPosition }) => {
  return (
    <div id="mySwitch">
      <input
        type="checkbox"
        id="switch"
        checked={checked}
        onChange={onChange}
      />
      <div className="app">
        <div className="container-switch">
          <label
            id="switchLanguage"
            className={scrollYPosition < 600 ? "bg-white" : ""}
            htmlFor="switch"
          >
            <div className="toggle"></div>
            <div className="language">
              <p className="english">EN</p>
              <p className="espanol">ES</p>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
};
